import {
  PlayArrow as ContinueIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';

import type { Wizard } from '../types/wizard';

interface WizardCardProps {
  wizard: Wizard;
  onDelete: (wizardId: string) => void;
}

export const WizardCard = ({ wizard, onDelete }: WizardCardProps) => {
  const navigate = useNavigate();
  const isDraft = wizard.status === 'draft';

  const handleClick = () => {
    if (isDraft) {
      // For drafts, go straight to continue editing
      navigate(`/a/${wizard.appId}/wiz/${wizard.baseId}/form`);
    } else {
      // For completed wizards, view config
      navigate(`/a/${wizard.appId}/wiz/${wizard.baseId}`);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/a/${wizard.appId}/wiz/${wizard.baseId}/form`);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(wizard.id);
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        '&:hover': { boxShadow: 4 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      onClick={handleClick}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6">{wizard.name}</Typography>
          {isDraft && <Chip label="Draft" size="small" color="warning" variant="outlined" />}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip label={wizard.sourceLabel} size="small" color="primary" />
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>
            →
          </Typography>
          <Chip label={wizard.destinationLabel} size="small" color="secondary" />
        </Box>

        <Typography variant="caption" color="text.secondary">
          Updated: {new Date(wizard.updatedAt).toLocaleDateString()}
        </Typography>
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {isDraft ? (
            <Button
              size="small"
              startIcon={<ContinueIcon />}
              onClick={handleEdit}
              variant="contained"
            >
              Continue
            </Button>
          ) : (
            <IconButton size="small" onClick={handleEdit} aria-label="edit">
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <IconButton size="small" onClick={handleDelete} color="error" aria-label="delete">
          <DeleteIcon fontSize="small" color="error" />
        </IconButton>
      </CardActions>
    </Card>
  );
};
